import { useEffect, useRef, useState, useContext } from 'react';
import Spinner from '../Spinner';
import Stepper from './MyStepper';
import FundingFilters from '../../utils/FundingFilters';
import FundingItem from './FundingItem';
import styles from './Form.module.css';
import FundingModal from '../FundingModal';
import RestartAltOutlinedIcon from '@mui/icons-material/RestartAltOutlined';
import { toast } from 'react-toastify';
import { Tooltip } from '@mui/material';
import GradientSpinner from '../GradientSpinner';
import AiWriteIcon from './AiWriteIcon';
import { FaRegStopCircle } from 'react-icons/fa';
import { Context } from '../../AuthContext';
import { useOutletContext } from 'react-router-dom';
import { useCategory } from '../../CategoryContext';
import { getPersonalVerticalsLimit, getPlanTier } from '../../config/planLimits';
import WarningTooltip from '../WarningTooltip';
import UpgradeButton from '../UpgradeButton';
import WatchGuideLink from '../WatchGuideLink/WatchGuideLink';

const VERTICAL_LIMIT_TOOLTIP =
  'You\u2019ve reached your Personal Vertical limit. Delete an existing Vertical to create a new one.';

const VERTICAL_LIMIT_REPLACEMENT_EXHAUSTED_TOOLTIP =
  'You\u2019ve reached your Personal Vertical limit. Additional replacements become available after your allowance resets. Check the Draft or My Verticals pages to see your reset timing. Upgrade to Full Access for expanded workspace capacity.';

const PAID_VERTICAL_LIMIT_REPLACEMENT_EXHAUSTED_TOOLTIP =
  'You\u2019ve reached your Personal Vertical limit. Additional replacements become available after your allowance resets. Check the Draft or My Verticals pages to see your reset timing.';

const regions = [
  'Global / International',
  'United States',
  'Canada',
  'Europe',
  'Asia',
  'Africa',
  'Oceania',
  'South America',
];

const Form = ({ navigateOnSubmit }) => {
  const [activeTab, setSetActiveTab] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [areaOfResearchInterest, setAreaOfResearchInterest] = useState('');
  const [verticalName, setVerticalName] = useState('');
  const [verticalAbstract, setVerticalAbstract] = useState('');
  const [numberOfPapers, setNumberOfPapers] = useState(20);
  const [numberOfPatents, setNumberOfPatents] = useState(10);
  const [numberOfGrants, setNumberOfGrants] = useState(10);
  const [includeOpenGrants, setIncludeOpenGrants] = useState(true);
  const [includeForecastGrants, setIncludeForecastGrants] = useState(true);
  const [includeClosedGrants, setIncludeClosedGrants] = useState(false);
  const [openFundingModal, setOpenFundingModal] = useState(false);
  const [fundingOrgrinalFilterValues, setFundingOriginalFilterValues] = useState({});
  const [fundingFilterValues, setFundingFilterValues] = useState({});
  const [fundingFilterEnabled, setFundingFilterEnabled] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [verticalNameLoading, setVerticalNameLoading] = useState(false);
  const [verticalAbstractLoading, setVerticalAbstractLoading] = useState(false);
  const abortControllerVerticalAbstractRef = useRef(null);
  const { user, authLoading } = useContext(Context);
  const [selectedRegion, setSelectedRegion] = useState('United States');
  const { subscriptionStatus } = useOutletContext() || {};
  const { verticals } = useCategory();
  const personalCount = (verticals || []).filter(
    (v) => v.status === 'Pending' || v.status === 'Created'
  ).length;
  const verticalsLimit = getPersonalVerticalsLimit(subscriptionStatus);
  const limitReached = personalCount >= verticalsLimit;
  const planTier = getPlanTier(subscriptionStatus);
  const [replacementState, setReplacementState] = useState(null);
  const replacementsExhausted =
    replacementState &&
    replacementState.limit != null &&
    replacementState.used >= replacementState.limit;
  const verticalLimitTooltip = !replacementsExhausted
    ? VERTICAL_LIMIT_TOOLTIP
    : planTier === 'free'
      ? VERTICAL_LIMIT_REPLACEMENT_EXHAUSTED_TOOLTIP
      : PAID_VERTICAL_LIMIT_REPLACEMENT_EXHAUSTED_TOOLTIP;

  useEffect(() => {
    if (!user?.uid || !limitReached) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const token = await user.getIdToken();
        const res = await fetch(`${process.env.REACT_APP_FLASK_WEBSERVER}replacement_state`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok && !cancelled) {
          setReplacementState(await res.json());
        }
      } catch (err) {
        console.error('Failed to fetch replacement state', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.uid, planTier, limitReached]);

  const fetchFundingFilters = async (uid) => {
    const token = await user.getIdToken();
    const url = process.env.REACT_APP_FLASK_WEBSERVER + 'get_user_funding_filters';
    const res = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    const filters = await res.json();
    const values = filters || {};
    setFundingFilterValues(values);
    setFundingOriginalFilterValues(values);
    setFundingFilterEnabled(Object.keys(values).reduce((acc, k) => ({ ...acc, [k]: true }), {}));
  };

  useEffect(() => {
    if (authLoading || !user) return;

    fetchFundingFilters(user.uid).finally(() => {
      setIsLoading(false);
    });
  }, [authLoading, user]);

  const resetFilters = () => {
    setNumberOfGrants(10);
    setNumberOfPatents(10);
    setNumberOfPapers(20);
    setIncludeClosedGrants(false);
    setSelectedRegion('United States');
    setFundingFilterValues(fundingOrgrinalFilterValues);
    setFundingFilterEnabled(
      Object.keys(fundingOrgrinalFilterValues).reduce((acc, k) => ({ ...acc, [k]: true }), {})
    );
  };

  const updateFundingFilter = async (key, value, setShow) => {
    setFundingFilterValues((prev) => ({ ...prev, [key]: value }));
    setFundingFilterEnabled((prev) => ({ ...prev, [key]: true }));
  };

  const toggleFilter = (key, enabled) => {
    setFundingFilterEnabled((prev) => ({ ...prev, [key]: enabled }));
  };

  const updateFundingFilterDb = async () => {
    try {
      // keep only filters where the checkbox is enabled
      const enabledFilters = Object.keys(fundingFilterValues).reduce((acc, key) => {
        if (fundingFilterEnabled[key]) {
          acc[key] = fundingFilterValues[key];
        }
        return acc;
      }, {});
      const token = await user.getIdToken();

      const response = await fetch(process.env.REACT_APP_FLASK_WEBSERVER + 'edit_user', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ funding_filters: enabledFilters }),
      });
      if (response.status === 200) {
        await fetchFundingFilters(user.currentUser.uid);
        toast.success('Funding filters have been updated successfully!');
      }
    } catch (error) {
      console.error('something happened wrong:', error);
    } finally {
      setOpenFundingModal(false);
    }
  };

  const addTask = async () => {
    const now = new Date();
    const payload = {
      query: verticalName,
      abstract: verticalAbstract,
      user: {
        uid: user.uid,
        name: user.displayName,
        email: user.email,
      },
      OpportunityStatus: [
        includeOpenGrants && 'open',
        includeForecastGrants && 'forecasted',
        includeClosedGrants && 'closed',
      ].filter(Boolean),
      numberOfPatents: numberOfPatents,
      numberOfRelevantPapers: numberOfPapers,
      numberOfGrants: numberOfGrants,
      status: 'Pending',
      time: now.toLocaleString(),
      fundingFilters: Object.keys(fundingFilterValues).reduce((acc, key) => {
        if (fundingFilterEnabled[key]) {
          acc[key] = fundingFilterValues[key];
        }
        return acc;
      }, {}),
      region: selectedRegion,
    };
    const config = {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${await user.getIdToken()}`,
        'Content-type': 'application/json',
      },
      body: JSON.stringify(payload),
    };
    await fetch(process.env.REACT_APP_FLASK_WEBSERVER + 'tasks', config);
  };

  const generateVerticalName = async () => {
    try {
      setVerticalNameLoading(true);
      const token = await user.getIdToken();
      const config = {
        method: 'POST',
        body: JSON.stringify({
          areaOfNicheResearch: areaOfResearchInterest,
        }),
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      };
      const response = await fetch(process.env.REACT_APP_FLASK_WEBSERVER + 'generate-name', config);
      const result = await response.json();
      setVerticalName(result.verticalName);
    } catch (error) {
      console.error(error);
    } finally {
      setVerticalNameLoading(false);
    }
  };

  const generateAbstract = async () => {
    try {
      setVerticalAbstractLoading(true);
      abortControllerVerticalAbstractRef.current = new AbortController();
      const token = await user.getIdToken();

      const config = {
        method: 'POST',
        body: JSON.stringify({
          areaOfNicheResearch: areaOfResearchInterest,
        }),
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        signal: abortControllerVerticalAbstractRef.current.signal,
      };
      const response = await fetch(
        process.env.REACT_APP_FLASK_WEBSERVER + 'generate-abstract',
        config
      );
      const result = await response.json();
      setVerticalAbstract(result.verticalAbstract);
    } catch (error) {
      console.error(error);
    } finally {
      setVerticalAbstractLoading(false);
    }
  };

  const cancelRequestVerticalAbstract = () => {
    if (abortControllerVerticalAbstractRef.current) {
      abortControllerVerticalAbstractRef.current.abort('User canceled the request');
    }
  };

  return isLoading ? (
    <Spinner />
  ) : (
    <div style={{ width: '85%' }}>
      {activeTab === 0 && (
        <form
          className="step1"
          onSubmit={(e) => {
            if (limitReached) {
              e.preventDefault();
              return;
            }
            setSetActiveTab(1);
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <div style={{ textAlign: 'left' }}>
              <p style={{ margin: 0, padding: 0, fontWeight: 700 }}>
                Personal Verticals: {personalCount} of {verticalsLimit}
              </p>
              <p
                style={{
                  lineHeight: 1,
                  margin: 0,
                  padding: 0,
                  fontSize: '12px',
                  color: 'gray',
                }}
              >
                Consists of both Private Verticals and My Verticals
              </p>
            </div>
            <Stepper activeTab={activeTab} setSetActiveTab={setSetActiveTab} />
          </div>
          <div style={{ display: 'flex', marginY: '8px 0' }}>
            {planTier === 'free' && <UpgradeButton sx={{ margin: '0 0 12px 0' }} />}
          </div>
          <textarea
            rows={5}
            className={`input-style w-full ${styles.field}`}
            placeholder="Area of Research Interest Prompt"
            value={areaOfResearchInterest}
            disabled={limitReached}
            onChange={(e) => setAreaOfResearchInterest(e.target.value)}
          />
          <p className={styles.note}>
            Briefly describe the area or concept your vertical will explore. This doesn’t have to be
            the final name — this is just a starting point for naming and summary generation.
          </p>

          <div
            className={styles.row}
            style={{
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              margin: '20px 0px 10px 0px',
            }}
          >
            <div>
              <div className={styles.row}>
                <button
                  type="button"
                  className={styles.button}
                  onClick={generateVerticalName}
                  style={{ display: 'flex', alignItems: 'center', gap: 5 }}
                  disabled={
                    limitReached || verticalNameLoading || areaOfResearchInterest.length < 10
                      ? true
                      : false
                  }
                >
                  {verticalNameLoading ? (
                    <div>
                      <GradientSpinner size={'16px'} />
                    </div>
                  ) : (
                    <AiWriteIcon />
                  )}
                  Generate via AI
                </button>

                <label className={styles.label}>
                  AI Suggested Vertical Name (Get an AI-generated name you can edit)
                </label>
              </div>
            </div>
            <div>
              <label className={styles.label}>{verticalName.length} / 100</label>
            </div>
          </div>

          <input
            type="text"
            className={`input-style w-full ${styles.field}`}
            placeholder="Vertical Name"
            value={verticalName}
            disabled={limitReached || verticalNameLoading}
            onChange={(e) => setVerticalName(e.target.value)}
            maxLength={100}
            required
          />
          <p className={styles.note} style={{ marginTop: '5px' }}>
            Provide the official name for your vertical. You may enter your own title directly, or
            use AI to generate a suggested name based on your area of research interest. All text
            remains fully editable.
          </p>

          <div
            className={styles.row}
            style={{
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              margin: '20px 0px 10px 0px',
            }}
          >
            <div>
              <div className={styles.row}>
                {verticalAbstractLoading ? (
                  <button
                    type="button"
                    className={styles.button}
                    onClick={cancelRequestVerticalAbstract}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                      <span>
                        {verticalAbstractLoading ? (
                          <GradientSpinner size={'16px'} />
                        ) : (
                          <AiWriteIcon />
                        )}
                      </span>
                      <span>Stop Generation</span>
                      <FaRegStopCircle size={'16px'} />
                    </div>
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled={
                      limitReached || verticalAbstractLoading || areaOfResearchInterest.length < 10
                        ? true
                        : false
                    }
                    className={styles.button}
                    style={{ display: 'flex', alignItems: 'center', gap: 5 }}
                    onClick={generateAbstract}
                  >
                    <AiWriteIcon /> Generate via AI
                  </button>
                )}

                <label className={styles.label}>
                  AI Suggested Vertical Summary (Get an AI-generated summary you can edit)
                </label>
              </div>
            </div>
            <div>
              <label className={styles.label} style={{ margin: 0 }}>
                {verticalAbstract.length} / 2500
              </label>
            </div>
          </div>

          <textarea
            rows={10}
            className={`input-style w-full ${styles.field}`}
            placeholder="Vertical Summary"
            value={verticalAbstract}
            disabled={limitReached || verticalAbstractLoading}
            onChange={(e) => setVerticalAbstract(e.target.value)}
            maxLength={2500}
            required
          />

          <p className={styles.note}>
            Write a detailed summary to describe the scope and focus of your vertical. You may draft
            this yourself, or use AI to generate an initial version informed by your area of
            research interest. You can edit and refine it at any time.
          </p>
          <div className={styles.footer}>
            <WarningTooltip message={verticalLimitTooltip} disabled={!limitReached}>
              <span style={{ display: 'block', width: '100%' }}>
                <input
                  className="input-submit"
                  style={{ padding: '8px 0', margin: '8px 0px' }}
                  type="submit"
                  value="Next"
                  disabled={verticalAbstractLoading || verticalAbstractLoading || limitReached}
                />
              </span>
            </WarningTooltip>
          </div>
        </form>
      )}

      {activeTab === 1 && (
        <form
          className={styles.grid}
          onSubmit={async (e) => {
            e.preventDefault();
            if (
              JSON.stringify(fundingFilterValues) !== JSON.stringify(fundingOrgrinalFilterValues)
            ) {
              setOpenFundingModal(true);
            } else {
              await addTask();
              await navigateOnSubmit();
              setSubmitting(false);
            }
          }}
        >
          <div>
            <div
              style={{
                display: 'flex',
              }}
            >
              <Stepper activeTab={activeTab} setSetActiveTab={setSetActiveTab} />
            </div>
            <div className={styles.mb10}>
              <div
                className={styles.row}
                style={{ justifyContent: 'space-between', margin: '10px 0px 5px 0px' }}
              >
                <p className={styles.label}>Name your Vertical</p>
                <div>
                  <label className={styles.label} style={{ margin: 0 }}>
                    {verticalName.length} / 100
                  </label>
                </div>
              </div>

              <input
                type="text"
                className={`input-style w-full ${styles.field}`}
                placeholder="[Pre-Dissemination Matching for ECA users]"
                value={verticalName}
                maxLength={100}
                onChange={(e) => setVerticalName(e.target.value)}
              />
            </div>

            <div className={styles.mb10}>
              <div
                className={styles.row}
                style={{ justifyContent: 'space-between', margin: '20px 0px 5px 0px' }}
              >
                <p className={styles.label}>Here is your summary (up to 2500 characters)</p>
                <div>
                  <label className={styles.label} style={{ margin: 0 }}>
                    {verticalAbstract.length} / 2500
                  </label>
                </div>
              </div>

              <textarea
                rows={10}
                className={`input-style w-full ${styles.field}`}
                value={verticalAbstract}
                onChange={(e) => setVerticalAbstract(e.target.value)}
                maxLength={2500}
                placeholder="Vertical Summary"
                required
              />
            </div>

            <div className={styles.row}>
              <label className={styles.label} style={{ fontWeight: 'bold' }}>
                Number of Papers:
              </label>
              <input
                type="number"
                className="input-style"
                style={{ width: '80px' }}
                placeholder="20"
                max={50}
                min={1}
                //value={1}
                value={numberOfPapers}
                onChange={(e) => setNumberOfPapers(e.target.valueAsNumber)}
                required
              />

              <label className={styles.label} style={{ fontWeight: 'bold' }}>
                Number of Patents:
              </label>
              <input
                type="number"
                className="input-style"
                style={{ width: '80px' }}
                placeholder="10"
                max={20}
                min={10}
                value={numberOfPatents}
                onChange={(e) => setNumberOfPatents(e.target.valueAsNumber)}
                required
              />
            </div>

            <div>
              <FundingModal
                isLoading={submitting}
                open={openFundingModal}
                handleClose={(event) => {
                  event.stopPropagation();
                  if (!submitting) {
                    setOpenFundingModal(false);
                  }
                }}
                onSave={async (event) => {
                  event.stopPropagation();
                  setSubmitting(true);
                  if (
                    JSON.stringify(fundingFilterValues) !==
                    JSON.stringify(fundingOrgrinalFilterValues)
                  ) {
                    await updateFundingFilterDb();
                  }
                  await addTask();
                  await navigateOnSubmit();
                  setSubmitting(false);
                  setOpenFundingModal(false);
                }}
                onNotSave={async (event) => {
                  event.stopPropagation();
                  setSubmitting(true);
                  await addTask();
                  await navigateOnSubmit();
                  setOpenFundingModal(false);
                  setSubmitting(false);
                }}
                dialogTitle="Save Funding Filters"
                dialogContentText="You have edited some of the Funding Filters. Would you like them to be saved in your Account to use in the future RNAI Search Quries?"
              />

              <input
                className="input-submit"
                style={{ padding: '8px 0', margin: '8px 0px' }}
                type="submit"
                value="Create Vertical"
                disabled={limitReached}
              />
            </div>

            <p
              style={{
                textAlign: 'left',
                fontSize: '12px',
                lineHeight: '1.3',
              }}
            >
              This process usually completes within a few minutes, but may take up to 15 minutes.
              You’re free to continue using other parts of the platform while it runs.
            </p>
          </div>

          <div>
            <div style={{ textAlign: 'left' }}>
              <WatchGuideLink
                videoId="setting-your-filter-preferences"
                label="Watch Guide: Filters"
              />
            </div>
            <div className={styles.funding_filters}>
              <div className={styles.filtersHeader}>
                <div className={styles.boldText}>Funding Filters</div>
                <Tooltip
                  title="Reset Filters"
                  slotProps={{
                    popper: {
                      modifiers: [
                        {
                          name: 'offset',
                          options: {
                            offset: [0, -10],
                          },
                        },
                      ],
                    },
                  }}
                  placement="top"
                  arrow
                >
                  <div className={styles.toggleButton}>
                    <RestartAltOutlinedIcon
                      className={`${styles.cursorPointer} ${styles.refreshIcon}`}
                      onClick={() => resetFilters()}
                    />
                  </div>
                </Tooltip>
              </div>
              <div className={styles.filtersRow}>
                <div>
                  <span className={styles.label} style={{ fontWeight: 'bold' }}>
                    Number of Grants
                  </span>
                </div>
                <div>
                  <input
                    type="number"
                    className="input-style"
                    style={{ width: '70px' }}
                    placeholder="10"
                    value={numberOfGrants}
                    min={1}
                    max={20}
                    required
                    onChange={(e) => setNumberOfGrants(e.target.valueAsNumber)}
                  />
                </div>
              </div>
              <div className={styles.filtersList}>
                <div className={styles.row}>
                  <input
                    type="checkbox"
                    checked={includeOpenGrants}
                    onChange={(e) => setIncludeOpenGrants(e.target.checked)}
                  />
                  <label className={styles.label}>Include Open Grants</label>
                </div>
                <div className={`${styles.row} ${styles.checkboxRow}`}>
                  <input
                    type="checkbox"
                    checked={includeForecastGrants}
                    onChange={(e) => setIncludeForecastGrants(e.target.checked)}
                  />
                  <label className={styles.label}>Include Forecast Grants</label>
                </div>

                <div className={`${styles.row} ${styles.checkboxRow}`}>
                  <input
                    type="checkbox"
                    checked={includeClosedGrants}
                    onChange={(e) => setIncludeClosedGrants(e.target.checked)}
                  />
                  <label className={styles.label}>Include Closed Grants</label>
                </div>

                {FundingFilters.map((item) => (
                  <FundingItem
                    item={item}
                    fundingFilters={fundingFilterValues}
                    updateFilters={updateFundingFilter}
                    enabledMap={fundingFilterEnabled}
                    toggleFilter={toggleFilter}
                  />
                ))}
              </div>

              <div className={styles.filtersHeader}>
                <div className={styles.boldText}>Organizations/Jobs/Events Filters</div>
              </div>
              <p className={styles.label} style={{ fontWeight: 'bold', marginLeft: '10px' }}>
                Geographic Region
              </p>
              <div className={styles.filtersList}>
                {regions.map((region) => (
                  <div className={`${styles.row} ${styles.checkboxRow}`} key={region}>
                    <input
                      type="checkbox"
                      checked={selectedRegion === region}
                      onChange={() => setSelectedRegion(region)}
                    />
                    <label className={styles.label}>{region}</label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </form>
      )}
    </div>
  );
};

export default Form;
